/**
 * Un paso de la bitácora de acciones.
 *
 * Se guarda YA legible y YA redactado: `label` es una línea en español lista para leerse, escrita
 * en el momento de capturar el paso. Es lo que hace que el JSON crudo se entienda sin traductor y
 * que quien arregla el bug no tenga que interpretar códigos.
 */
export interface TraceEvent {
  /** Secuencia de la pestaña. Ordena aunque dos pasos caigan en el mismo milisegundo. */
  seq: number
  kind: TraceEventKind
  /** Epoch en milisegundos. */
  at: number
  /** Una línea en español, p. ej. `Clic en "Registrar pago"`. */
  label: string
  /** Patrón de la ruta donde ocurrió (`/customer/:id`), no la URL con el id. */
  screen: string
  /** Detalle estructurado, ya redactado. Se pliega en la lectura. */
  data?: Record<string, unknown>
}

export type TraceEventKind = 'nav' | 'click' | 'http' | 'error' | 'toast' | 'session'

/** El entorno donde ocurrió todo. Viaja junto a los pasos cuando se manda un reporte. */
export interface TraceContext {
  url: string
  route: string
  sessionId: string
  appVersion: string
  userAgent: string
  platform: string
  language: string
  utcOffsetMinutes: number
  viewport: { width: number; height: number }
  startedAt: string
  capturedAt: string
}

export interface TracePayload {
  context: TraceContext
  events: TraceEvent[]
}

//* Cuántos pasos se conservan. Sesenta cubren de sobra lo que alguien recuerda haber hecho antes de
//* que algo fallara, y mantienen el paquete en un tamaño que se manda sin pensarlo.
export const TRACE_CAPACITY = 60

//* Techo del buffer completo y de un paso suelto. El segundo evita que una respuesta enorme se coma
//* el presupuesto entero y deje la bitácora con un solo paso.
export const TRACE_MAX_BYTES = 48 * 1024
//* Sube de 1536 a 3072 desde que el paso lleva el cuerpo de la petición: un alta de cotización
//* redactada ronda el kilobyte, y con el tope anterior se descartaba justo en las peticiones que
//* más dicen. El tope TOTAL no se mueve — el buffer sigue podando por los dos extremos.
export const TRACE_MAX_EVENT_BYTES = 3072

//* Sube cuando cambie la forma de `TraceEvent`. Al arrancar, un paquete guardado con otra versión
//* se descarta en vez de intentar migrarlo: es evidencia efímera, no datos que valga la pena salvar.
export const TRACE_VERSION = 1

//* Clave propia en sessionStorage. NUNCA cerca de 'renda-user', que es la sesión del usuario.
export const TRACE_STORAGE_KEY = 'renda-trace'

//* Un paquete más viejo que esto no se restaura: pertenece a otro rato de trabajo y mezclarlo con
//* lo de ahora haría que el reporte cuente una historia que no pasó.
export const TRACE_MAX_AGE_MS = 30 * 60 * 1000
